<template>
	<ui-root v-if="loadState === 'loaded'" :part="form" :data="data" :display="{ mode: modal ? 'modal':'window' }" :errs="errors" :actions="controls"
			 :readonly="false" ref="formComponent" @close="dialogClose"></ui-root>
</template>

<script setup lang="ts">
import {ref, computed, reactive, watch, onUnmounted} from "vue";
import UiRoot from "@fd/containers/UiRoot.vue";
import {loadFormDefinition, loadSourceType, setEndpointId, SourceType} from "@t/meta/SourceType.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition.ts";
import FormPart from "@fd/containers/FormPart.vue";
import type {FormData, FormPart as FormPartType} from "@t/fd/FormPart.ts";
import type {ErrorMap} from "@fd/PartInjectKeys.ts";
import {connectEventBus, injectEnv} from "@fd/Environment.ts";
import {DataWithMeta, removeMeta} from "@t/fd/WithMeta.ts";
import {ApiError, ApiErrorResponse} from "@t/util/ApiError.ts";
import {addContextParams, injectContext} from "@t/fd/AppContext.ts";
import {CommandHandlerResult} from "@fd/Types.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import get from "lodash.get";

const props = withDefaults(defineProps<{
	srcType: number;
	srcSubType?: number;
	modal?: boolean;
	/** The record will be saved to the backend/DB */
	store?: boolean;

	/** Data to be edited specified as an ID (pk) or object */
	pk?: string|number;
	initialData?: FormData;

	extraErrors?: ErrorMap;
	/** ID of the save button ("create", "add", "save", ...) */
	button: string;
	/* handlers to "hack" behavior, argument `edit` is true if the endpoint should be for loading an existing object */
	buildEndpoint?: (endpoint:string, type:SourceType, edit?: boolean) => string;
}>(), {
	modal: true,
	store: true,
	extraErrors: () => [],
	button: "create",
	buildEndpoint: (endpoint:string, t:SourceType, edit?: boolean) => endpoint,
});

type LoadingState = "initial" | "loading" | "loaded" | "notfound" | "forbidden" | "error";
const loadState = ref<LoadingState>("initial");

const env = injectEnv();
const type = ref<SourceType>(null);
const form = ref<InterfaceRoot<FormDefinition>>(null);
const formComponent = ref<InstanceType<typeof FormPart> | null>(null);
const data = ref<FormData>(props.initialData ?? {});
const errors:ErrorMap = reactive<ErrorMap>([...props.extraErrors]);
const backendErrors: ErrorMap = reactive<ErrorMap>([]);
const controls = computed<FormPartType>(() => {
	return dialogControls(env, ["cancel", props.button]);
});

// Supported bus events
// "closed" - close dialog (no record was created)
// "created" - close dialog (the record was created and is available as a argument of this event)
const bus = connectEventBus("RowObjectCreate");
const ctx = injectContext();
const ns = useNavigationStore();

watch(
	[backendErrors, () => props.extraErrors],
	() => { errors.splice(0, errors.length, ...backendErrors, ...props.extraErrors); },
	{ deep: true }
);

/**
 * Editing of the data can start:
 * a) by initializing a new object (no pk or data provided)
 *    - form can be loaded separately from data or as bundled meta
 * b) by loading existing object (pk provided)
 *    - form can be loaded separately from data or as bundled meta
 * c) by taking a preloaded object (data provided)
 *    - form loading still required (provided object doesn't have it bundled)
 */
watch([() => props.srcType, () => props.srcSubType], async () => {
	loadState.value = "loading";
	try {
		clearErrors();
		if (type.value == null || type.value.id !== props.srcType) {
			type.value = await loadSourceType(env, props.srcType);
		}

		if (props.initialData == null) {
			// No data provided => variant a) or b)
			const metaSupport = !!type.value.meta;	// If endpoints support metadata they can load data and corresponding form at once
			let ep = props.pk ? buildEditEndpoint() : buildInitEndpoint();

			if (metaSupport) ep = env.net.urlParam(ep, "meta", "true");
			else form.value = await loadFormDefinition(env, type.value.form);

			const resp = await env.net.fetch(ep);
			if (resp.ok) {
				if (metaSupport) {
					const dataMeta:DataWithMeta<FormData> = await resp.json();
					form.value = dataMeta.META.form;
					data.value = removeMeta(dataMeta);
				} else {
					data.value = await resp.json();
				}
				loadState.value = "loaded";
			} else {
				console.error("Cannot load form data with META info from " + ep);
				loadState.value = "error";
			}
		} else {
			// Data provided => variant c)
			form.value = await loadFormDefinition(env, type.value.form);
			data.value = props.initialData as FormData;
			loadState.value = "loaded";
		}
	} catch (ex) {
		loadState.value = "error";
	}
}, { immediate: true });


function clearErrors():void {
	backendErrors.length = 0;
}

function buildInitEndpoint():string {
	let ep = "api" + type.value.init;
	const initCtx = {...ctx.value };
	ep = addContextParams(ep, initCtx);
	ep = props.buildEndpoint(ep, type.value, false);
	return ep;
}

function buildEditEndpoint():string {
	let ep = setEndpointId("api"+type.value.edit, props.pk);
	ep = props.buildEndpoint(ep, type.value, true);
	return ep;
}

let saving = false;
bus.onCommand(["cancel", props.button], async (cmd:string):Promise<CommandHandlerResult> => {
	if (cmd === "cancel") {
		bus.fire("closed");
		return "ok";
	}
	if (!saving) {
		try {
			saving = true;
			document.body.style.cursor = "progress";
			let ep:string, method:string;
			const idFromData = get(data.value, type.value.idProperty ?? "id");
			if (idFromData) {
				// Saving existing record
				ep = setEndpointId("api" + type.value.edit, idFromData);
				method = "PUT";
			} else {
				// Creating a new record
				ep = "api" + type.value.create;
				method = "POST";
			}

			if (!props.store) ep = env.net.urlParam(ep, "dry", true);	// dry save (validation only)
			const resp = await env.net.fetch(ep, method, data.value);
			clearErrors();
			if (resp.ok) {
				// Successful save ... body should be the new version of the record
				data.value = await resp.json();
				if (props.store && data.value['id'] != null) {
					ns.display(type.value.id, data.value['id']);	// update history with newer (data, e.g. name)
				}
				bus.fire("created", data.value);
				return "ok";
			} else {
				// Backend returned non-20xx status code (e.g. BAD_REQUEST)
				const er = await resp.json() as ApiErrorResponse;
				processSubmitProblems(er.problems);
				return "fail";
			}
		} catch(err) {
			const errText = err.toString();
			clearErrors();
			let problems:ApiError[] = [{ type: "ERROR", level: "SEVERE", message:errText }];
			processSubmitProblems(problems);
			console.error(err);
			return "fail";
		} finally {
			saving = false;
			document.body.style.cursor = "auto";
		}
	} else return "fail";
});

function processSubmitProblems(problems:ApiError[]):void {
	if (!Array.isArray(problems)) return;
	backendErrors.push(...problems);
}

function dialogClose() {
	bus.fire("closed");
}
</script>

<style>

</style>